/**
 * U-0023 (`docs/concept-file-grants.md` §12.2): which words in a run of text name a file in the work
 * area's repository, so every surface links the same ones. `findReferenceTokens` decides item keys;
 * this decides paths, and the two never overlap (KL-13 drops a key inside a path).
 *
 * Plain text in, ranges out. The caller has already left out what is not prose: a fenced code block
 * and a link's text (the editor's decoration, `repositoryPathLinks.ts`), and says when the run is an
 * inline code span, which links only when it holds exactly the path (KL-16's rule).
 *
 * ⛔ A path is relative to the repository root and names a file: two or more segments, the last with
 * an extension. No scheme, no leading `/` or `~`, no `.` or `..` segment. A bare file name
 * (`CLAUDE.md`) is not a path: which folder it is in would be a guess.
 */

export interface IRepositoryPathRange {
  /** Offsets into the run's text. */
  start: number;
  end: number;
  /** The path as written, `docs/concept-file-grants.md`. */
  path: string;
}

import { DEVICE_HANDLE, FOLDER_NAME, type IFileReferenceFolder } from './file-reference-folder.js';

const SEGMENT = '[A-Za-z0-9._@+-]+';
const CANDIDATE = new RegExp(`${SEGMENT}(?:/${SEGMENT})+`, 'g');
/** A character that, before a candidate, makes it the tail of something longer: a URL, `~/`, `/abs`. */
const CONTINUES_BEFORE = /[A-Za-z0-9._@+\-/:~\\]/;
/** A file name with an extension that holds a letter (`a.md`, `.gitignore`), not `2.5`. */
const FILE_NAME = /^(?:[^.].*\.|\.)[A-Za-z0-9]*[A-Za-z][A-Za-z0-9]*$/;
/** At most this long, as the machine's own cap (`COMMIT_DIFF_MAX_PATH_LENGTH`). */
export const REPOSITORY_PATH_MAX_LENGTH = 4096;

/** Whether `path` is one this module would link: the rules above, on the whole string. */
export function isRepositoryPath(path: string): boolean {
  if (!path || path.length > REPOSITORY_PATH_MAX_LENGTH) return false;
  if (!new RegExp(`^${SEGMENT}(?:/${SEGMENT})+$`).test(path)) return false;
  const segments = path.split('/');
  if (segments.some((segment) => segment === '.' || segment === '..')) return false;
  // An npm package (`@featit/shared/…`) or a host without its scheme (`example.com/a.md`).
  if (segments[0].startsWith('@')) return false;
  if (hostLike(segments[0])) return false;
  return FILE_NAME.test(segments[segments.length - 1]);
}

/** `example.com`, `docs.github.com`: a first segment that reads as a host. A folder rarely does. */
function hostLike(segment: string): boolean {
  return /^[^.].*\.(com|org|net|io|dev|app|de|co|uk|eu|ai|gov|edu)$/i.test(segment);
}

/**
 * The repository paths in one run of text, in order. `inCodeSpan`: the run is an inline code span,
 * which is a path only when it is exactly one.
 */
export function findRepositoryPaths(text: string, inCodeSpan = false): IRepositoryPathRange[] {
  if (!text || !text.includes('/')) return [];
  if (inCodeSpan) {
    const trimmed = text.trim();
    if (!isRepositoryPath(trimmed)) return [];
    const start = text.indexOf(trimmed);
    return [{ start, end: start + trimmed.length, path: trimmed }];
  }
  const ranges: IRepositoryPathRange[] = [];
  const pattern = new RegExp(CANDIDATE.source, 'g');
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    const start = match.index;
    if (start > 0 && CONTINUES_BEFORE.test(text[start - 1])) continue;
    // A sentence's full stop is not the file's: `see docs/a.md.`
    const path = match[0].replace(/\.+$/, '');
    const end = start + path.length;
    if (!isRepositoryPath(path)) continue;
    ranges.push({ start, end, path });
  }
  return ranges;
}

/**
 * Where a relative link inside a file opens: resolved against the folder of `fromPath`, or `null`
 * when it leaves the repository or is not a path this module links (FG-85).
 */
export function resolveRepositoryPath(fromPath: string, href: string): string | null {
  if (!href || /^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith('#')) return null;
  const withoutFragment = href.split(/[?#]/)[0];
  if (!withoutFragment) return null;
  const base = withoutFragment.startsWith('/') ? [] : fromPath.split('/').slice(0, -1);
  const parts = [...base];
  for (const segment of withoutFragment.split('/')) {
    if (segment === '' || segment === '.') continue;
    if (segment === '..') {
      if (parts.length === 0) return null;
      parts.pop();
    } else {
      parts.push(segment);
    }
  }
  const resolved = parts.join('/');
  return isRepositoryPath(resolved) ? resolved : null;
}

/** A path in text, and the folder it names when it is a qualified reference. */
export interface IFileReferenceRange extends IRepositoryPathRange {
  /** Present for `<folder>@<device>:<path>`; absent for a plain path. */
  folder?: IFileReferenceFolder;
}

const QUALIFIED = new RegExp(
  `(${FOLDER_NAME})@(${DEVICE_HANDLE}):(${SEGMENT}(?:/${SEGMENT})*)`,
  'g',
);
/** A file name with an extension at the repository root: with its folder named, it is no guess. */
const ROOT_FILE = /^[A-Za-z0-9._+-]+$/;

/**
 * The device handle a machine's label gives: lower case, every run of anything but a letter or a
 * digit one `-`, trimmed, at most 63 characters; `device` for a label with none. The deployment
 * keeps one unique among a work area's folders of one name by a suffix (`macbook-pro-2`, FG-108).
 */
export function deviceHandle(label: string | null | undefined): string {
  const handle = (label ?? '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63)
    .replace(/-+$/g, '');
  return handle || 'device';
}

/**
 * FG-108: `base`, or the first of `base-2`, `base-3`, … that `taken` does not hold, within 63
 * characters: how a second folder of one name on a same-labelled device gets its own handle.
 */
export function freeDeviceHandle(base: string, taken: ReadonlySet<string | null>): string {
  let handle = base;
  for (let suffix = 2; taken.has(handle); suffix += 1) {
    handle = `${base.slice(0, 63 - String(suffix).length - 1)}-${suffix}`;
  }
  return handle;
}

/** Whether `path` is a path a qualified reference may name: a repository path, or a root file. */
export function isQualifiedFilePath(path: string): boolean {
  if (isRepositoryPath(path)) return true;
  if (path.length > REPOSITORY_PATH_MAX_LENGTH || !ROOT_FILE.test(path)) return false;
  if (path === '.' || path === '..') return false;
  return FILE_NAME.test(path);
}

/** The text of a qualified reference: `featit-app@macbook-pro:tasks/x.md`. */
export function fileReferenceText(folder: IFileReferenceFolder, path: string): string {
  return `${folder.name}@${folder.device}:${path}`;
}

/**
 * The file references in one run of text, in order: qualified references (§12.9.2) and plain paths
 * (§12.2), never overlapping. `inCodeSpan`: the run is an inline code span, which is a reference
 * only when it is exactly one.
 */
export function findFileReferences(text: string, inCodeSpan = false): IFileReferenceRange[] {
  if (!text || (!text.includes('/') && !text.includes('@'))) return [];
  if (inCodeSpan) {
    const trimmed = text.trim();
    const start = text.indexOf(trimmed);
    const qualified = qualifiedReferences(trimmed);
    if (qualified.length === 1 && qualified[0].start === 0 && qualified[0].end === trimmed.length) {
      return [{ ...qualified[0], start, end: start + trimmed.length }];
    }
    return findRepositoryPaths(text, true);
  }
  const qualified = qualifiedReferences(text);
  const plain = findRepositoryPaths(text).filter(
    (range) => !qualified.some((ref) => range.start < ref.end && ref.start < range.end),
  );
  return [...qualified, ...plain].sort((a, b) => a.start - b.start);
}

function qualifiedReferences(text: string): IFileReferenceRange[] {
  const ranges: IFileReferenceRange[] = [];
  const pattern = new RegExp(QUALIFIED.source, 'g');
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    const start = match.index;
    if (start > 0 && CONTINUES_BEFORE.test(text[start - 1])) continue;
    const [, name, device, written] = match;
    // A sentence's full stop is not the file's.
    const path = written.replace(/\.+$/, '');
    // A device handle runs into a `.` only in a host name (`git@github.com:…`), never here.
    const end = start + name.length + 1 + device.length + 1 + path.length;
    if (text[start + name.length + 1 + device.length] !== ':') continue;
    if (!isQualifiedFilePath(path)) continue;
    if (name === '.' || name === '..') continue;
    ranges.push({ start, end, path, folder: { name, device } });
  }
  return ranges;
}
